import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Chip,
    Tabs,
    Tab,
    Paper
} from '@mui/material';
import { VideoLibrary as VideoIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import FileUploadZone from '../../MediaLibrary/FileUploadZone';

interface VideoLessonEditorProps {
    content: any;
    onChange: (content: any) => void;
}

const VideoLessonEditor: React.FC<VideoLessonEditorProps> = ({ content, onChange }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [mediaFiles, setMediaFiles] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const dataProvider = useDataProvider();
    const notify = useNotify();

    const loadVideoFiles = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: { fileType: 'video' }
            });
            setMediaFiles(data);
        } catch (error) {
            notify('Fehler beim Laden der Videos', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        loadVideoFiles();
        setTabValue(0);
        setDialogOpen(true);
    };

    const handleSelectVideo = (file: any) => {
        onChange({
            ...content,
            videoFileId: file.id,
            videoFileName: file.originalFilename,
            videoUrl: `/api/media/${file.uuid}`,  // Will be converted to preview route by player
            videoType: 'url'  // Media library videos are direct URLs
        });
        setDialogOpen(false);
    };

    const handleUploadComplete = () => {
        notify('Video erfolgreich hochgeladen', { type: 'success' });
        // Reload video files and switch to selection tab
        loadVideoFiles();
        setTabValue(0);
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${Math.round(bytes / Math.pow(k, i) * 100) / 100} ${sizes[i]}`;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Video-Lektion</Typography>

            {content?.videoFileName ? (
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ausgewähltes Video:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <VideoIcon color="primary" />
                        <Typography variant="body1">{content.videoFileName}</Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleOpenDialog}
                        sx={{ mt: 2 }}
                    >
                        Anderes Video auswählen
                    </Button>
                </Box>
            ) : (
                <Button
                    variant="contained"
                    startIcon={<VideoIcon />}
                    onClick={handleOpenDialog}
                >
                    Video aus Medienbibliothek auswählen
                </Button>
            )}

            <TextField
                label="Video-Beschreibung (optional)"
                multiline
                rows={3}
                fullWidth
                value={content?.description || ''}
                onChange={(e) => onChange({ ...content, description: e.target.value })}
                helperText="Zusätzliche Informationen zum Video"
            />

            <TextField
                label="Externe Video-URL (optional)"
                fullWidth
                value={content?.externalUrl || ''}
                onChange={(e) => onChange({ ...content, externalUrl: e.target.value })}
                helperText="YouTube, Vimeo oder andere Video-URL als Alternative"
            />

            {/* Video Selection Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Video auswählen oder hochladen</DialogTitle>
                <DialogContent>
                    <Tabs
                        value={tabValue}
                        onChange={(e, newValue) => setTabValue(newValue)}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                        <Tab label="Aus Medienbibliothek auswählen" />
                        <Tab label="Neues Video hochladen" icon={<CloudUploadIcon />} iconPosition="start" />
                    </Tabs>

                    {tabValue === 0 && (
                        <Box>
                            {loading ? (
                                <Typography>Lade Videos...</Typography>
                            ) : mediaFiles.length === 0 ? (
                                <Typography>Keine Videos in der Medienbibliothek gefunden.</Typography>
                            ) : (
                                <List>
                                    {mediaFiles.map((file) => (
                                        <ListItemButton key={file.id} onClick={() => handleSelectVideo(file)}>
                                            <ListItem>
                                                <VideoIcon sx={{ mr: 2 }} color="action" />
                                                <ListItemText
                                                    primary={file.originalFilename}
                                                    secondary={
                                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                            <Chip label={formatFileSize(parseInt(file.size))} size="small" />
                                                            <Chip label={file.mimeType} size="small" />
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        </ListItemButton>
                                    ))}
                                </List>
                            )}
                        </Box>
                    )}

                    {tabValue === 1 && (
                        <Paper sx={{ p: 2 }}>
                            <FileUploadZone
                                onUploadComplete={handleUploadComplete}
                                categories={[]}
                            />
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VideoLessonEditor;
